// Angular
import {
  afterEveryRender,
  DestroyRef,
  Directive,
  ElementRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// Angular Material
import { LiveAnnouncer } from '@angular/cdk/a11y';
// The one file allowed to import MatSortModule / MatSortHeader (eslint.config.js):
// every table gets sorting through STANDARD_SORT_IMPORTS below.
import {
  MatSort,
  MatSortHeader,
  MatSortModule,
  Sort,
} from '@angular/material/sort';

/** Material's description for every sort button when none is set. */
const MATERIAL_DEFAULT_DESCRIPTION = 'Sort';

/**
 * Adds the screen-reader support every standard sort header needs on top of
 * Material's `mat-sort-header` (docs/table-header-standard.md):
 *
 * - Describes the sort button by its column ("Sort by Court") instead of
 *   Material's plain "Sort". A `sortActionDescription` set in the template wins.
 * - Announces each sort change ("Sorted by Court, ascending"), which Material
 *   does not do (WCAG 4.1.3 Status Messages).
 *
 * It applies to every `mat-sort-header` in a template that imports
 * {@link STANDARD_SORT_IMPORTS}, so tables never import `MatSortModule` directly.
 */
@Directive({
  selector: '[mat-sort-header]',
  standalone: true,
})
export class SortHeaderA11yDirective {
  private readonly header = inject(MatSortHeader);
  private readonly sort = inject(MatSort);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly announcer = inject(LiveAnnouncer);

  /** The column label last read from the header. */
  private label = '';

  /** The description this directive last set, so it can be updated when the label changes. */
  private appliedDescription: string | null = null;

  constructor() {
    // Labels can come from bindings ({{ col.title }}), so re-read after each render.
    afterEveryRender(() => this.syncDescription());

    this.sort.sortChange
      .pipe(takeUntilDestroyed(inject(DestroyRef)))
      .subscribe(change => this.announce(change));
  }

  /** Describes the sort button by the column label, unless the template set its own description. */
  private syncDescription(): void {
    const label = this.readLabel();
    if (!label || label === this.label) return;
    this.label = label;

    const current = this.header.sortActionDescription;
    if (
      current !== MATERIAL_DEFAULT_DESCRIPTION &&
      current !== this.appliedDescription
    ) {
      return;
    }
    this.appliedDescription = `Sort by ${label}`;
    this.header.sortActionDescription = this.appliedDescription;
  }

  /** Announces a sort change when it is this header's column. */
  private announce(change: Sort): void {
    if (change.active !== this.header.id) return;
    const label = this.readLabel() || change.active;
    const message = change.direction
      ? `Sorted by ${label}, ${change.direction === 'asc' ? 'ascending' : 'descending'}`
      : `Sorting by ${label} removed`;
    this.announcer.announce(message, 'polite');
  }

  /** The header text with whitespace collapsed. */
  private readLabel(): string {
    return (this.host.nativeElement.textContent ?? '')
      .replace(/\s+/g, ' ')
      .trim();
  }
}

/**
 * Everything a component needs for sortable table headers. Import this instead
 * of `MatSortModule`, so every `mat-sort-header` gets {@link SortHeaderA11yDirective}.
 */
export const STANDARD_SORT_IMPORTS = [MatSortModule, SortHeaderA11yDirective];
